import { Button, Flex, IllustratedMessage, Pagination, Progress, Stack, Switch } from '@eleveo/components-react';
import { CircleIcon, FilterIcon, SearchNoneIcon } from '@eleveo/icons-react';
// eslint-disable-next-line
import { today } from '@internationalized/date';
import React, { useEffect, useMemo, useState } from 'react';
// eslint-disable-next-line import/no-extraneous-dependencies
import { useDateFormatter } from 'react-aria';
import { useTranslation } from 'react-i18next';

import { useAuth } from '../../app/AuthorizationProvider';
import { Header } from '../../components/LayoutNext/Header';
import { Content, Panel, SidebarWrapper } from '../../components/LayoutNext/LayoutNext';
import { ActiveFilters as ActiveFiltersNew } from '../../components/LayoutNext/components/ActiveFilters';
import { Center } from '../../components/LayoutNext/components/Center';
import { getActiveFilterValue, kpiItems } from '../../components/LayoutNext/components/CheckboxFilterTile';
import { useSidebar } from '../../components/layout/Sidebar';
import {
  useGetAgentsReportsQuery,
  useGetSupervisedAgentsAllQuery,
  useGetSupervisedTeamsQuery,
  useGetTeamsReportsQuery,
} from '../../graphql/components';
import { SupervisedAgentsAllResult, SupervisedTeamsResult } from '../../graphql/types';
import { useFilter } from '../../utils/filter';
import { fullNameOfAgent } from '../../utils/helpers';
import { ReportsAdherenceExport } from './ReportsAdherenceExport';
import {
  ReportsAdherenceFilterSidebar,
  ReportsAdherenceFilterSidebarState,
  getDefaultReportsAdherenceSidebarFilter,
} from './ReportsAdherenceFilterSidebar';
import { TeamStatisticsTable } from './TeamStatisticsTable';
import { UserStatisticsTable } from './UserStatisticsTable';
import { KPI_OPTIONS_PROPS, REPORTS_GRANULARITY_TYPES } from './config';
import { CONTAINER_TAG, LIST_PAGINATION_TAG, TITLE_TAG, TOGGLE_FILTER_BUTTON_TAG } from './constants';

import type { ReportsKpiType, Values } from '../../types';

export const KpiOptionComponent = ({ testId, type }: { testId?: string; type: Values<typeof ReportsKpiType> }) => {
  const { t } = useTranslation();

  const { color, nameKey } = KPI_OPTIONS_PROPS[type];

  return (
    <Flex as="span" alignItems="baseline" gap={10} inline testId={testId}>
      <CircleIcon
        className="text-sm"
        style={{
          color,
        }}
      />
      <strong>{t(nameKey)}</strong>
    </Flex>
  );
};

export const ReportsAdherenceScene: React.FC = () => {
  const { t } = useTranslation();

  const { userId, timezone } = useAuth();

  const { openSidebar, closeSidebar, isSidebarOpen } = useSidebar();

  const [reportsViewGranularity, setReportsViewGranularity] = useState<Values<typeof REPORTS_GRANULARITY_TYPES>>(
    REPORTS_GRANULARITY_TYPES.users
  );
  const [isFilterSidebarOpen, setIsFilterSidebarOpen] = useState(false);
  const [preOpenedItemIds, setPreOpenedItemIds] = useState<string[]>(['1']);
  const [kpiVisibility, setKpiVisibility] = useState({} as Record<Values<typeof ReportsKpiType>, boolean>);
  const [pagination, setPagination] = React.useState({
    page: 1,
    pageSize: 25,
  });

  const isAgentsGranularityView = reportsViewGranularity === REPORTS_GRANULARITY_TYPES.users;
  const isTeamsGranularityView = reportsViewGranularity === REPORTS_GRANULARITY_TYPES.teams;

  const defaultRangeFilter = useMemo(
    () => ({
      start: today(timezone).subtract({ days: 14 }), // - 14 days, to have exact 2 weeks range
      end: today(timezone).subtract({ days: 1 }), // yesterday
    }),
    [timezone]
  );

  // Filter state
  const {
    filter: sidebarFilter,
    setFilter: setSidebarFilter,
    isFilterActive: isSidebarFiltersActive,
  } = useFilter<ReportsAdherenceFilterSidebarState>(getDefaultReportsAdherenceSidebarFilter());

  const visibleKpi = useMemo(
    () => sidebarFilter.kpi.filter((kpi) => kpiVisibility[kpi]),
    [kpiVisibility, sidebarFilter.kpi]
  );

  useEffect(() => {
    setKpiVisibility((state) => {
      const newState: Record<string, boolean> = {};
      sidebarFilter.kpi.forEach((kpi) => {
        newState[kpi] = state[kpi] ?? true; // if new KPI enabled, it should be automatically set to be visible
      });

      return newState;
    });
  }, [sidebarFilter.kpi]);

  const { data: agentsResult, loading: agentsLoading } = useGetSupervisedAgentsAllQuery({
    fetchPolicy: 'network-only',
    variables: {
      supervisorId: userId,
    },
  });

  const { data: teamsData, loading: teamsLoading } = useGetSupervisedTeamsQuery({
    fetchPolicy: 'network-only',
    variables: {
      supervisorId: userId,
    },
  });

  const loading = agentsLoading || teamsLoading;

  const handleOpenFilterSidebarItem = (preOpenItemId: string) => {
    setIsFilterSidebarOpen(true);
    setPreOpenedItemIds([preOpenItemId]);

    if (!isFilterSidebarOpen) {
      openSidebar();
      setIsFilterSidebarOpen(true);
    }
  };

  const handleCancelFilterSidebar = () => {
    closeSidebar();
    setIsFilterSidebarOpen(false);
    setPreOpenedItemIds([]);
  };

  const handleToggleFilterSidebar = (skipResetPreOpenedState = false) => {
    if (!skipResetPreOpenedState) {
      setPreOpenedItemIds([]);
    }

    if (isFilterSidebarOpen) {
      closeSidebar();
      setIsFilterSidebarOpen(false);
    } else {
      setIsFilterSidebarOpen(true);
      openSidebar();
    }
  };

  useEffect(() => {
    if (!isSidebarFiltersActive && !agentsLoading && !teamsLoading) {
      handleToggleFilterSidebar(true); // do not skip preOpenedItemIds state so that accordion with KPIs is pre-opened
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [agentsLoading, teamsLoading]);

  const handlePageChange = (page: number, pageSize?: number) => {
    setPagination((state) => ({
      ...state,
      page,
      pageSize: pageSize ?? state.pageSize,
    }));
  };

  const resetPagination = () => {
    handlePageChange(1);
  };

  const handleFilter = (filter: ReportsAdherenceFilterSidebarState) => {
    // Will call API twice, because it is not batched, React v18 should change that

    handleCancelFilterSidebar();

    resetPagination();
    setSidebarFilter(filter);
  };

  const formatter = useDateFormatter({ dateStyle: 'short', timeZone: timezone });

  const countOfVisibleKpi = useMemo(() => Object.values(kpiVisibility).filter(Boolean).length, [kpiVisibility]);

  const switchViewGranularity = (isTeamsGranularitySelected: boolean) => {
    setReportsViewGranularity(
      isTeamsGranularitySelected ? REPORTS_GRANULARITY_TYPES.teams : REPORTS_GRANULARITY_TYPES.users
    );
  };

  const ActiveFilters: React.FC = () => (
    <ActiveFiltersNew
      config={[
        {
          dataTestId: 'reports-adherence-active-sidebar-filter-kpi',
          defaultValue: [],
          id: '1',
          key: 'kpi',
          name: t('reports.kpi'),
          value: getActiveFilterValue(kpiItems(t), sidebarFilter.kpi),
        },
        {
          dataTestId: 'reports-adherence-active-sidebar-filter-range',
          defaultValue: null,
          id: '2',
          key: 'dateRange',
          name: t('date.dateRange'),
          value:
            sidebarFilter.dateRange?.start && sidebarFilter.dateRange?.end
              ? `${formatter.format(sidebarFilter.dateRange.start?.toDate(timezone))} - ${formatter.format(
                  sidebarFilter.dateRange?.end.toDate(timezone)
                )}`
              : '',
        },
        {
          dataTestId: 'reports-adherence-active-sidebar-filter-teams',
          defaultValue: [],
          id: '3',
          key: 'teams',
          name: t('team.team', { count: 2 }),
          value: getActiveFilterValue(teamsData?.supervisedTeams, sidebarFilter.teams),
        },
        {
          dataTestId: 'reports-adherence-active-sidebar-filter-agents',
          defaultValue: [],
          id: '4',
          key: 'agents',
          name: t('person.people'),
          value: getActiveFilterValue(
            agentsResult?.supervisedAgents?.agents.map((agent) => ({ ...agent, name: fullNameOfAgent(agent) })),
            sidebarFilter.agents
          ),
        },
      ]}
      cancelFilterSidebar={handleCancelFilterSidebar}
      openFilterSidebarItem={handleOpenFilterSidebarItem}
      setSidebarFilter={setSidebarFilter}
      sidebarFilter={sidebarFilter}
    />
  );

  const filterVariables = {
    ...pagination,
    agentIds: sidebarFilter.agents.length ? sidebarFilter.agents.toString() : undefined,
    from: sidebarFilter.dateRange?.start.toString() ?? defaultRangeFilter.start.toString(),
    page: pagination.page - 1, // BE indexes pages starting from 0
    teamIds: sidebarFilter.teams.length ? sidebarFilter.teams.toString() : undefined,
    to: sidebarFilter.dateRange?.end.toString() ?? defaultRangeFilter.end.toString(),
  };

  // only one of following 2 queries should be used based on value of `reportsViewGranularity`
  const { data: { agentsReports } = {}, loading: isLoadingAgentsReports } = useGetAgentsReportsQuery({
    variables: filterVariables,
    fetchPolicy: 'network-only',
    skip: !sidebarFilter.kpi.length || isTeamsGranularityView,
  });

  const { data: { teamsReports } = {}, loading: isLoadingTeamsReports } = useGetTeamsReportsQuery({
    variables: {
      ...pagination,
      from: sidebarFilter.dateRange?.start.toString() ?? defaultRangeFilter.start.toString(),
      page: pagination.page - 1, // BE indexes pages starting from 0
      teamIds: sidebarFilter.teams.length ? sidebarFilter.teams.toString() : undefined,
      to: sidebarFilter.dateRange?.end.toString() ?? defaultRangeFilter.end.toString(),
    },
    fetchPolicy: 'network-only',
    skip: !sidebarFilter.kpi.length || isAgentsGranularityView,
  });

  const isLoadingReports = isLoadingAgentsReports || isLoadingTeamsReports;

  const emptyReportsData =
    (isAgentsGranularityView && !agentsReports?.result.usersStatistics.length) ||
    (isTeamsGranularityView && !teamsReports?.result.teamStatistics.length);

  const GranularityViewSwitch = () => (
    <Switch isSelected={isTeamsGranularityView} onChange={switchViewGranularity}>
      {t('reports.groupByTeams')}
    </Switch>
  );

  return (
    <>
      <Header title={t('scheduleAdherence.adherence')} testId={TITLE_TAG} />

      {(loading || isLoadingReports) && <Progress withOverlay />}

      <Panel>
        <Stack className="w-full" spacing={15}>
          <ActiveFilters />

          <Stack className="mis-auto" spacing={15}>
            <ReportsAdherenceExport
              from={filterVariables.from}
              to={filterVariables.to}
              teamIds={filterVariables.teamIds}
              agentIds={filterVariables.agentIds}
              reportsViewGranularity={reportsViewGranularity}
              isExportDisabled={emptyReportsData || countOfVisibleKpi === 0}
              visibleKpi={visibleKpi}
            />
            <Button
              isSelected={isFilterSidebarOpen}
              onClick={() => handleToggleFilterSidebar()}
              testId={TOGGLE_FILTER_BUTTON_TAG}
            >
              <FilterIcon />
              {t('filter.filters')}
            </Button>
          </Stack>
        </Stack>
      </Panel>

      <Content testId={CONTAINER_TAG}>
        {countOfVisibleKpi === 0 && !loading && (
          <Center>
            <IllustratedMessage
              description={t('reports.selectFilterCriteria')}
              heading={t('reports.noReportGenerated')}
              icon={<SearchNoneIcon />}
            />
          </Center>
        )}

        {isSidebarFiltersActive && !loading && !isLoadingReports && countOfVisibleKpi > 0 && emptyReportsData && (
          <Center>
            <IllustratedMessage heading={t('filter.noResultsMatch')} icon={<SearchNoneIcon />} />
          </Center>
        )}

        {isSidebarFiltersActive &&
          !loading &&
          countOfVisibleKpi > 0 &&
          isAgentsGranularityView &&
          !!agentsReports?.result.usersStatistics.length && (
            <Flex className="min-w-full h-full" gap={20} column>
              <GranularityViewSwitch />

              <UserStatisticsTable
                agentsReports={agentsReports}
                dateRange={sidebarFilter.dateRange ?? defaultRangeFilter} // if no date range selected, use default range - from start of current month to yesterday
                visibleKpi={visibleKpi}
              >
                <Pagination
                  onPageChange={handlePageChange}
                  selectedPage={pagination.page}
                  selectedPageSize={pagination.pageSize}
                  testId={LIST_PAGINATION_TAG}
                  totalItems={agentsReports.metadata.totalRecords}
                />
              </UserStatisticsTable>
            </Flex>
          )}

        {isSidebarFiltersActive &&
          !loading &&
          countOfVisibleKpi > 0 &&
          isTeamsGranularityView &&
          !!teamsReports?.result.teamStatistics.length && (
            <Flex className="min-w-full h-full" gap={20} column>
              <GranularityViewSwitch />

              <TeamStatisticsTable
                teamsReports={teamsReports}
                dateRange={sidebarFilter.dateRange ?? defaultRangeFilter} // if no date range selected, use default range - from start of current month to yesterday
                visibleKpi={visibleKpi}
              >
                <Pagination
                  onPageChange={handlePageChange}
                  selectedPage={pagination.page}
                  selectedPageSize={pagination.pageSize}
                  testId={LIST_PAGINATION_TAG}
                  totalItems={teamsReports.metadata.totalRecords}
                />
              </TeamStatisticsTable>
            </Flex>
          )}
      </Content>

      <SidebarWrapper>
        {isSidebarOpen && isFilterSidebarOpen && (
          <ReportsAdherenceFilterSidebar
            handleCancel={handleCancelFilterSidebar}
            preOpenedItemIds={preOpenedItemIds}
            agentsResult={{ data: agentsResult } as SupervisedAgentsAllResult}
            teamsResult={{ data: teamsData } as SupervisedTeamsResult}
            setPreOpenedItemIds={setPreOpenedItemIds}
            setSidebarFilter={handleFilter}
            sidebarFilter={sidebarFilter}
          />
        )}
      </SidebarWrapper>
    </>
  );
};
